<template>
  <div style="display:inline-block">
    <a
      style="color:#6A1B9A; padding: 0px 6px"
      :tooltip="$t('editOrderCharge.editChargeTooltip')"
      @click="openDialog()"
    >
      <i class="material-icons">attach_money</i>
    </a>

    <v-layout row justify-center>
      <v-dialog v-model="dialog" persistent max-width="480px">
        <v-card>
          <form action="" @submit.prevent="submitForm()">
            <v-card-title>
              <v-flex xs11>
                <span class="headline"><strong>{{ $t('editOrderCharge.editChargeTitle') }} <span class="text-primary ml-2">#{{ r.theOrderID }}</span></strong></span>
              </v-flex>
              <v-flex xs1>
                <v-btn flat @click.native="closeDialog()" class="close">
                  <span aria-hidden="true"><i class="material-icons" style="font-size:24px;vertical-align:middle;">close</i></span>
                </v-btn>
              </v-flex>
            </v-card-title>

            <v-card-text v-if="loading">
              <div class="text-xs-center" style="padding: 24px 0;">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </v-card-text>

            <v-card-text v-else>
              <v-alert type="info" :value="true" style="margin-bottom:12px;">
                {{ $t('editOrderCharge.alreadyChargedHint') }}
              </v-alert>

              <v-layout wrap>
                <v-flex xs12 sm6 class="px-2">
                  <v-text-field
                    :label="$t('editOrderCharge.itemPriceUsdLabel')"
                    v-model.number="formData.item_price"
                    :error-messages="errors.collect('Item price')"
                    v-validate="'required|decimal:2'"
                    data-vv-name="Item price"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="px-2">
                  <v-text-field
                    :label="$t('editOrderCharge.shippingUsdLabel')"
                    v-model.number="formData.internal_shipping_handling"
                    :error-messages="errors.collect('Shipping')"
                    v-validate="'required|decimal:2'"
                    data-vv-name="Shipping"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="px-2">
                  <v-text-field
                    :label="$t('columns.quantity')"
                    type="number"
                    v-model.number="formData.quantity"
                    :error-messages="errors.collect('Quantity')"
                    v-validate="'required|integer|min_value:1'"
                    data-vv-name="Quantity"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="px-2" v-if="r.coupon">
                  <v-text-field
                    :label="$t('editOrderCharge.discountUsdLabel')"
                    v-model.number="formData.discount"
                    :error-messages="errors.collect('Discount')"
                    v-validate="'decimal:2'"
                    data-vv-name="Discount"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 class="px-2">
                  <strong>{{ $t('editOrderCharge.newTotalLabel') }} </strong> ${{ computedTotal.toFixed(2) }}
                  <span style="color:#777"> {{ $t('editOrderCharge.wasLabel', { amount: Number(originalTotal).toFixed(2) }) }}</span>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                style="height:auto;color:#989595;"
                @click.native="closeDialog()"
                outline
                color="normal"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                type="submit"
                :loading="saving"
                :disabled="loading"
                style="height:auto"
                outline
                class="blue-logout rtl-mr-1-5pct"
              >{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </form>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'

export default {
  props: ['r', 'getResults'],

  $_veeValidate: {
    validator: 'new'
  },

  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      formData: {},
      originalTotal: 0
    }
  },

  computed: {
    computedTotal() {
      const itemPrice = Number(this.formData.item_price) || 0
      const shipping = Number(this.formData.internal_shipping_handling) || 0
      const qtt = Number(this.formData.quantity) || 0
      const tax = Number(this.formData.tax) || 0
      const nationalTax = Number(this.formData.national_tax) || 0
      const delivery = Number(this.formData.delivery_price) || 0
      const fee = Number(this.formData.kreenex_fee) || 0
      const insurance = Number(this.formData.insurance) || 0
      const qttCharge = Number(this.formData.quantity_charge) || 0
      const localDelivery = Number(this.formData.local_delivery) || 0
      const discount = Number(this.formData.discount) || 0

      // Per-unit shipping: item price AND internal shipping are both charged per unit, matching
      // newOrder.vue's calculateTotalAmount() — the screen that sets every order's total_amount
      // in the first place, since every order passes through "process new order" once. Confirmed
      // against live data: 76 of 79 existing orders with qty>1 and shipping>0 match this formula;
      // only 2 match a flat (non-scaled) shipping variant, which is the actual minority/anomaly.
      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.formData.is_repackage) {
        total += Number(this.formData.repackage_price) || 0
      }
      if (this.formData.is_hold) {
        total += Number(this.formData.hold_price) || 0
      }
      if (Number(this.formData.other_service_price) > 0) {
        total += Number(this.formData.other_service_price)
      }
      if (discount > 0) {
        total -= discount
      }

      return total
    }
  },

  methods: {
    openDialog() {
      this.dialog = true
      this.loading = true
      const token = localStorage.getItem('access_token')

      axios
        .get(`api/show_order_detail/${this.r.order_id}`, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.formData = { ...response.data.data[0] }
          this.originalTotal = Number(this.formData.total_amount) || 0
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.dialog = false
          swal(this.$t('common.error'), (error.response && error.response.data.message) || this.$t('editOrderCharge.failedToLoadOrderDetails'), 'error')
        })
    },

    closeDialog() {
      this.dialog = false
      this.formData = {}
      this.originalTotal = 0
      this.$validator.reset()
    },

    submitForm() {
      this.$validator.validateAll().then((res) => {
        if (res) {
          this.confirmAndSubmit()
        }
      })
    },

    confirmAndSubmit() {
      const newTotal = this.computedTotal
      const diff = Math.round((newTotal - this.originalTotal) * 100) / 100
      const credit = Number(this.formData.credit) || 0

      if (diff === 0) {
        this.submit()
        return
      }

      let text
      if (diff > 0) {
        text = credit >= diff
          ? this.$t('editOrderCharge.increaseWithBalanceText', { from: this.originalTotal.toFixed(2), to: newTotal.toFixed(2), diff: diff.toFixed(2) })
          : this.$t('editOrderCharge.increaseInsufficientBalanceText', { from: this.originalTotal.toFixed(2), to: newTotal.toFixed(2), diff: diff.toFixed(2) })
      } else {
        text = this.$t('editOrderCharge.decreaseText', { from: this.originalTotal.toFixed(2), to: newTotal.toFixed(2), diff: Math.abs(diff).toFixed(2) })
      }

      swal({
        title: this.$t('common.areYouSure'),
        text,
        icon: 'warning',
        buttons: true
      }).then((confirmed) => {
        if (confirmed) {
          this.submit()
        }
      })
    },

    submit() {
      this.saving = true
      const token = localStorage.getItem('access_token')

      const fields = [
        'item_price', 'tax', 'item_weight', 'internal_shipping_handling', 'tmp_shipping',
        'delivery_price', 'price_weight', 'kreenex_fee', 'insurance', 'quantity_charge',
        'discount', 'national_tax', 'is_repackage', 'repackage_price', 'is_hold', 'hold_price',
        'other_service_price', 'local_delivery', 'process_note', 'customer_process_note',
        'item_type', 'from_date', 'to_date', 'warehouse_date', 'description',
        'the_tl_price', 'the_aed_price', 'the_cny_price', 'the_gbp_price', 'the_eur_price',
        'transportation_type', 'quantity', 'color', 'size', 'note', 'brand'
      ]

      const formData = new FormData()
      fields.forEach((key) => {
        const value = this.formData[key]
        if (value !== null && value !== undefined) {
          formData.append(key, value)
        }
      })
      formData.append('total_amount', this.computedTotal.toFixed(2))

      axios
        .post(`api/orders/${this.r.order_id}`, formData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.saving = false
          this.closeDialog()
          this.getResults()

          const message = response.data.message
          if (message && typeof message === 'object' && message.adjustment_note) {
            swal(this.$t('editOrderCharge.orderUpdatedTitle'), message.adjustment_note, 'success')
          } else {
            swal({ title: this.$t('common.done'), icon: 'success', timer: 1200, buttons: false })
          }
        })
        .catch((error) => {
          this.saving = false
          swal(this.$t('common.error'), (error.response && error.response.data.message) || this.$t('editOrderCharge.failedToUpdateOrder'), 'error')
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/editOrderCharge.vue